import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Snackbar,
  TextField, Autocomplete, CircularProgress, Stack,
  IconButton, Menu, MenuItem, Checkbox, ListItemText, Tooltip, Divider,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CancelIcon from '@mui/icons-material/Cancel';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { TableKit, toHebrewNumeral } from '@NP-Webber/crm-kit';
import { formatHebrewDate } from '../../utils/hebrewDate';
import ImportSection from '../../components/ImportSection.jsx';
import GemaraSelectionEditor, {
  EMPTY_TRACTATE_ENTRY,
  selectionToEntries,
  entriesToSelection,
  isGemaraEntriesValid,
} from '../../components/GemaraSelectionEditor.jsx';
import GemaraExamineEditDialog from '../../components/GemaraExamineEditDialog.jsx';
import api from '../../services/api.js';
import RegistrationsDashboard from './RegistrationsDashboard.jsx';
import {
  normalizeGemaraSelection,
  summarizeGemaraSelection,
  formatGemaraSelectionText,
  countDafimInRanges,
  totalDafimInSelection,
} from '../../utils/gemaraSelection.js';
import AdminPageHeader from '../../next/AdminPageHeader.jsx';
import { useQuickCreate } from '../../next/quickCreate';

const IS_OPEN_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const SOURCE_LABELS = { ivr: '📞 ימות', web: '🌐 ווב', import: '📥 ייבוא' };

/** האם ההרשמה מסומנת כנבחן בפועל — זהה ללוגיקת עמודת «נבחן». */
function isRegistrationExamined(row) {
  if (!row || row.status !== 'registered') return false;
  if (IS_OPEN_GEMARA) return (row.gemaraSelection || []).some((s) => s.examined);
  return !!row.examined;
}

function examinedMarkLabel(row) {
  return isRegistrationExamined(row) ? 'כן' : 'לא';
}

const STATUS_COLORS = { registered: 'success', cancelled: 'error' };
const STATUS_LABELS = { registered: 'רשום', cancelled: 'בוטל' };

/* ─── כל העמודות האפשריות ─── */
const ALL_COLUMNS = [
  {
    key: '_memberFirstName', title: 'שם פרטי', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  {
    key: '_memberLastName', title: 'שם משפחה', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  {
    key: '_memberIdNumber', title: 'ת.ז. חבר', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  {
    key: '_memberPhone', title: 'טלפון חבר', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  {
    key: '_memberCity', title: 'עיר חבר', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  {
    key: '_memberKolel', title: 'כולל', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  ...(IS_OPEN_GEMARA
    ? [{
        key: '_gemaraText',
        title: 'חומר',
        sortable: false,
        filterable: true,
        render: (_, row) => {
          const text = formatGemaraSelectionText(row.gemaraSelection, toHebrewNumeral);
          return text || '—';
        },
        exportRender: (_, row) => formatGemaraSelectionText(row.gemaraSelection) || '',
      }, {
        key: '_totalDafim',
        title: 'סה״כ דפים',
        sortable: true,
        filterable: true,
        render: (v, row) => {
          const total = v ?? totalDafimInSelection(row.gemaraSelection);
          return total > 0 ? total : '—';
        },
        exportRender: (v, row) => {
          const total = v ?? totalDafimInSelection(row.gemaraSelection);
          return total > 0 ? String(total) : '';
        },
      }]
    : [{
        key: '_examName', title: 'מבחן', sortable: true, filterable: true,
        render: (v) => v || '—',
        exportRender: (v) => v || '',
      }]
  ),
  {
    key: '_examCode', title: 'קוד מבחן', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  {
    key: '_kolelCode', title: 'קוד כולל', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
  {
    key: 'location', title: 'עיר בחינה', sortable: true, filterable: true,
    render: (v) => v || '—',
  },
  {
    key: '_examinedMark',
    title: 'נבחן',
    sortable: true,
    filterable: true,
    // render מוחלף ב-columnsWithActions לגרסה האינטראקטיבית
    render: (_, row) => IS_OPEN_GEMARA
      ? (row.gemaraSelection?.some((s) => s.examined) ? '✔' : '—')
      : (row.examined ? '✔' : '—'),
    exportRender: (_, row) => IS_OPEN_GEMARA
      ? (row.gemaraSelection?.filter((s) => s.examined).map((s) => s.tractate).join(', ') || 'לא')
      : (row.examined ? 'כן' : 'לא'),
  },
  {
    key: 'status', title: 'סטטוס', sortable: true, filterable: true,
    render: (v) => (
      <Chip label={STATUS_LABELS[v] || v} color={STATUS_COLORS[v] || 'default'} size="small" />
    ),
    exportRender: (v) => STATUS_LABELS[v] || v || '',
  },
  {
    key: 'source', title: 'מקור', sortable: false,
    render: (v) => SOURCE_LABELS[v] || v,
    exportRender: (v) => v === 'ivr' ? 'ימות' : v === 'web' ? 'ווב' : v === 'import' ? 'ייבוא' : v || '',
  },
  {
    key: 'personalCode', title: 'קוד אישי', sortable: false,
    render: (v, row) => (v ?? row?._memberPersonalCode) ?? '—',
    exportRender: (v, row) => String(v ?? row?._memberPersonalCode ?? ''),
  },
  {
    key: 'registeredAt', title: 'תאריך רישום', sortable: true,
    render: (v) => v ? formatHebrewDate(v) : '—',
    exportRender: (v) => v ? formatHebrewDate(v) : '',
  },
  {
    key: 'period', title: 'זמן', sortable: true, filterable: true,
    render: (v) => v || '—',
    exportRender: (v) => v || '',
  },
];

/* ─── עמודות נוספות של פרטי חבר — רק לייצוא ─── */
const EXTRA_MEMBER_COLUMNS = [
  { key: '_memberNumber', title: 'מספר חבר' },
  { key: '_memberFatherName', title: 'שם אב' },
  { key: '_memberFatherInLawName', title: 'שם חותן' },
  { key: '_memberPhoneAlt', title: 'טלפון נוסף' },
  { key: '_memberStreet', title: 'רחוב' },
  { key: '_memberBuilding', title: 'מספר בניין' },
  { key: '_memberApartment', title: 'דירה' },
  { key: '_memberKolel', title: 'כולל' },
  { key: '_memberPersonalCode', title: 'קוד אישי חבר' },
];

/* ─── עמודות ייצוא נבחנים (לבודקים) ─── */
const EXAMINED_EXPORT_COLUMNS = [
  { key: 'firstName', title: 'שם פרטי' },
  { key: 'lastName', title: 'שם משפחה' },
  { key: 'idNumber', title: 'ת.ז.' },
  { key: 'personalCode', title: 'קוד אישי' },
  { key: 'phone', title: 'טלפון' },
  { key: 'phoneAlt', title: 'טלפון נוסף' },
  { key: 'city', title: 'עיר' },
  { key: 'kolel', title: 'כולל' },
  { key: 'location', title: 'מיקום מבחן' },
  { key: 'examCode', title: 'קוד מבחן' },
  { key: 'examName', title: 'שם מבחן' },
  { key: 'score', title: 'ציון' },
  { key: 'notes', title: 'הערות' },
];

const DEFAULT_EXAMINED_EXPORT_COLUMNS = [
  'firstName', 'lastName', 'idNumber', 'phone', 'examCode', 'examName', 'score', 'notes',
];

const EXAM_TYPE_LABELS = { time: 'זמן', shimur: 'שימור', miktzoa: 'מקצוע' };

/* ברירת מחדל — עמודות שמוצגות בהתחלה */
const DEFAULT_VISIBLE = [
  '_memberFirstName', '_memberLastName', ...(IS_OPEN_GEMARA ? ['_gemaraText', '_totalDafim'] : ['_examName', '_examCode']),
  '_kolelCode', 'location', '_examinedMark', 'status', 'source', 'personalCode', 'registeredAt', '_actions',
];

const STORAGE_KEY = 'registrations_visible_columns';

function loadVisibleColumns() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      let parsed = JSON.parse(saved);
      // מיגרציה: שמות עמודות שהשתנו
      let changed = false;
      if (parsed.includes('memberId') && !parsed.includes('_memberName') && !parsed.includes('_memberFirstName')) {
        parsed = parsed.map((k) => k === 'memberId' ? '_memberName' : k);
        changed = true;
      }
      // מיגרציה: שם חבר מאוחד → שם פרטי + שם משפחה
      if (parsed.includes('_memberName') && !parsed.includes('_memberFirstName')) {
        parsed = parsed.flatMap((k) => (k === '_memberName' ? ['_memberFirstName', '_memberLastName'] : [k]));
        changed = true;
      }
      if (parsed.includes('gemaraSelection') && !parsed.includes('_gemaraText')) {
        parsed = parsed.map((k) => k === 'gemaraSelection' ? '_gemaraText' : k);
        changed = true;
      }
      if (parsed.includes('examId') && !parsed.includes('_examName')) {
        parsed = parsed.map((k) => k === 'examId' ? '_examName' : k);
        changed = true;
      }
      // מיגרציה: examined → _examinedMark
      if (parsed.includes('examined') && !parsed.includes('_examinedMark')) {
        parsed = parsed.map((k) => k === 'examined' ? '_examinedMark' : k);
        changed = true;
      }
      // מיגרציה: הוספת עמודת סה״כ דפים אחרי חומר (תלמודא)
      if (IS_OPEN_GEMARA && parsed.includes('_gemaraText') && !parsed.includes('_totalDafim')) {
        const idx = parsed.indexOf('_gemaraText');
        parsed = [...parsed.slice(0, idx + 1), '_totalDafim', ...parsed.slice(idx + 1)];
        changed = true;
      }
      if (changed) localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
      return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_VISIBLE;
}

export default function RegistrationsList() {
  useQuickCreate(() => handleRegOpen());
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [pinnedSearches, setPinnedSearches] = useState([]);
  const [examinedOnly, setExaminedOnly] = useState(false);

  /* ─── הרשמה חדשה ─────────────────────────────────────────── */
  const [regOpen, setRegOpen]           = useState(false);
  const [examOptions, setExamOptions]   = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [regLocation, setRegLocation]   = useState('');
  const [regSaving, setRegSaving]       = useState(false);
  const [regResult, setRegResult]       = useState(null);

  /* ─── בחירת גמרא (מצב open-gemara) ─── */
  const [gemaraEntries, setGemaraEntries] = useState([EMPTY_TRACTATE_ENTRY()]);

  /* ─── דיאלוג סימון נבחן (תלמודא — per tractate + עריכת טווחים) ─── */
  const [examineDialogRow, setExamineDialogRow]     = useState(null);
  const [examineSaving, setExamineSaving]           = useState(false);

  /* ─── הורדת PDF לנבחן בודד ─── */
  const [downloadingId, setDownloadingId] = useState(null);

  /* ─── עריכת הרשמה ─── */
  const [editTarget, setEditTarget] = useState(null);
  const [editGemaraEntries, setEditGemaraEntries] = useState([EMPTY_TRACTATE_ENTRY()]);
  const [editExam, setEditExam] = useState(null);
  const [editLocation, setEditLocation] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editExamOptions, setEditExamOptions] = useState([]);

  /* ─── ציון להרשמה ─── */
  const [gradeDialogRow, setGradeDialogRow] = useState(null);
  const [gradeScores, setGradeScores] = useState({});
  const [gradeSaving, setGradeSaving] = useState(false);
  const [gradeLoading, setGradeLoading] = useState(false);
  const [gradeError, setGradeError] = useState('');

  /* ─── סינון תקופה ─── */
  const [periods, setPeriods] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('_active'); // _active = הפעיל, _all = הכל, או period._id
  const [examCities, setExamCities] = useState([]);

  useEffect(() => {
    api.get('/exam-cities?active=1')
      .then(({ data }) => {
        const list = (data.data || []).map((c) => c.name).filter(Boolean);
        setExamCities(list);
      })
      .catch(() => setExamCities([]));
  }, []);

  /* ─── תאריך ייבוא ─── */
  const lastYear = new Date();
  lastYear.setFullYear(lastYear.getFullYear() - 1);
  const [importDate, setImportDate] = useState(lastYear.toISOString().slice(0, 10));
  const [importPeriodId, setImportPeriodId] = useState('');
  const [importUpdateMode, setImportUpdateMode] = useState(false);

  /* ─── דיאלוג ייצוא ─── */
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFullDetails, setExportFullDetails] = useState(false);
  const [materialExporting, setMaterialExporting] = useState('');
  const [examinedExporting, setExaminedExporting] = useState(false);
  const [examinedExportOpen, setExaminedExportOpen] = useState(false);
  const [examinedExportMode, setExaminedExportMode] = useState('examined');
  const [examinedExportFormat, setExaminedExportFormat] = useState('excel');
  const [examinedExportFileMode, setExaminedExportFileMode] = useState('single');
  const [examinedExportColumns, setExaminedExportColumns] = useState(() => [...DEFAULT_EXAMINED_EXPORT_COLUMNS]);
  const [examinedExtraBlankCols, setExaminedExtraBlankCols] = useState([]);
  const [examinedNewBlankCol, setExaminedNewBlankCol] = useState('');

  // טעינת זמני מבחנים
  useEffect(() => {
    api.get('/exam-periods').then(({ data }) => {
      const list = data.data || [];
      setPeriods(list);
      const active = list.find(p => p.isActive);
      if (active) {
        setPeriodFilter(active._id);
      } else if (list.length > 0) {
        // אין מועד פעיל — מציג את המועד האחרון אוטומטית
        const lastPeriod = list[list.length - 1];
        setPeriodFilter(lastPeriod._id);
      }
    }).catch(() => {});
  }, []);

  /* ─── בחירת עמודות ─── */
  const [visibleKeys, setVisibleKeys] = useState(loadVisibleColumns);
  const [anchorEl, setAnchorEl] = useState(null);

  const toggleColumn = (key) => {
    setVisibleKeys((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };
  const showAll = () => {
    const all = [...ALL_COLUMNS.map((c) => c.key), '_actions'];
    setVisibleKeys(all);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  };
  const resetDefaults = () => {
    setVisibleKeys(DEFAULT_VISIBLE);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_VISIBLE));
  };

  /* ─── חיפוש חי (debounce) ─── */
  const debounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setActiveSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const pinSearch = () => {
    const val = searchInput.trim();
    if (!val || pinnedSearches.includes(val)) return;
    setPinnedSearches((prev) => [...prev, val]);
    setSearchInput('');
    setActiveSearch('');
  };
  const removePinnedSearch = (val) => {
    setPinnedSearches((prev) => prev.filter((p) => p !== val));
  };
  const clearAllSearch = () => {
    setSearchInput('');
    setActiveSearch('');
    setPinnedSearches([]);
  };

  // טען מבחנים פתוחים בפתיחת הדיאלוג
  useEffect(() => {
    if (!regOpen) return;
    api.get('/exams', { params: { pageSize: 200, status: 'open' } })
      .then(({ data: res }) => setExamOptions(res.data || []))
      .catch(() => setExamOptions([]));
  }, [regOpen]);

  // חפש חברים לפי הקלדה
  useEffect(() => {
    if (!regOpen) return;
    const timer = setTimeout(() => {
      setMemberLoading(true);
      api.get('/members', { params: { pageSize: 50, search: memberSearch } })
        .then(({ data: res }) => setMemberOptions(res.data || []))
        .catch(() => setMemberOptions([]))
        .finally(() => setMemberLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, regOpen]);

  const handleRegOpen = () => {
    setRegOpen(true);
    setSelectedExam(null);
    setSelectedMembers([]);
    setRegLocation('');
    setRegResult(null);
    setGemaraEntries([EMPTY_TRACTATE_ENTRY()]);
  };

  /* ─── טוגל נבחן מהיר (Hilcheta — predefined) ─── */
  const handleToggleExamined = useCallback(async (row) => {
    const nextExamined = !row.examined;
    const applyExamined = (target, examined) => ({
      ...target,
      examined,
      _examinedMark: examinedMarkLabel({ ...target, examined }),
    });
    // עדכון אופטימיסטי מיידי
    setAllData((prev) => prev.map((r) => r._id === row._id ? applyExamined(r, nextExamined) : r));
    try {
      const { data: updated } = await api.put(`/registrations/${row._id}`, { examined: nextExamined });
      // עדכון מ-response הרשמי
      setAllData((prev) => prev.map((r) => (
        r._id === updated._id ? applyExamined(r, updated.examined) : r
      )));
    } catch {
      // rollback
      setAllData((prev) => prev.map((r) => (
        r._id === row._id ? applyExamined(r, row.examined) : r
      )));
      setSnackbar({ open: true, message: 'שגיאה בעדכון סטטוס בחינה', severity: 'error' });
    }
  }, []);

  /* ─── פתח דיאלוג סימון נבחן (Talmuda) ─── */
  const openExamineDialog = useCallback((row) => {
    setExamineDialogRow(row);
  }, []);

  /* ─── שמור סימון נבחן + טווחי דפים (Talmuda) ─── */
  const handleExamineDialogSave = async (gemaraSelection) => {
    if (!examineDialogRow) return;
    setExamineSaving(true);
    try {
      const { data: updated } = await api.put(`/registrations/${examineDialogRow._id}`, { gemaraSelection });
      setAllData((prev) => prev.map((r) => r._id === updated._id
        ? {
            ...r,
            gemaraSelection: updated.gemaraSelection,
            _gemaraText: formatGemaraSelectionText(updated.gemaraSelection) || '',
            _totalDafim: totalDafimInSelection(updated.gemaraSelection),
            _examinedMark: examinedMarkLabel({ ...r, gemaraSelection: updated.gemaraSelection }),
          }
        : r
      ));
      setSnackbar({ open: true, message: 'סטטוס בחינה עודכן', severity: 'success' });
      setExamineDialogRow(null);
    } catch {
      setSnackbar({ open: true, message: 'שגיאה בשמירת סטטוס בחינה', severity: 'error' });
    } finally {
      setExamineSaving(false);
    }
  };

  const handleEditOpen = (row) => {
    setEditTarget(row);
    setEditLocation(row.location || '');
    if (IS_OPEN_GEMARA) {
      setEditGemaraEntries(selectionToEntries(row.gemaraSelection));
    } else {
      setEditExam(row.examId || null);
      api.get('/exams', { params: { pageSize: 200, status: 'open' } })
        .then(({ data: res }) => setEditExamOptions(res.data || []))
        .catch(() => setEditExamOptions([]));
    }
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      const body = { location: editLocation };
      if (IS_OPEN_GEMARA) {
        body.gemaraSelection = entriesToSelection(editGemaraEntries);
      } else if (editExam) {
        body.examId = editExam._id;
      }
      await api.put(`/registrations/${editTarget._id}`, body);
      setSnackbar({ open: true, message: 'ההרשמה עודכנה בהצלחה', severity: 'success' });
      setEditTarget(null);
      loadData();
    } catch (err) {
      const msg = err.response?.data?.error || 'שגיאה בעדכון ההרשמה';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setEditSaving(false);
    }
  };

  /* ─── חילוץ string מ-periodId (יכול להיות object מ-populate או string) ─── */
  const extractPeriodId = (periodId) => {
    if (!periodId) return '';
    if (typeof periodId === 'object') return String(periodId._id || '');
    return String(periodId);
  };

  /* ─── פתח דיאלוג ציון להרשמה ─── */
  const openGradeDialog = useCallback(async (row) => {
    setGradeDialogRow(row);
    setGradeError('');
    setGradeLoading(true);
    try {
      const memberId = row.memberId?._id || row.memberId;
      const examId = row.examId?._id || row.examId;
      const examPeriodId = extractPeriodId(row.periodId);
      const gradeParams = { memberId, all: 1 };
      if (IS_OPEN_GEMARA && examPeriodId) gradeParams.examPeriodId = examPeriodId;
      else if (examId) gradeParams.examId = examId;
      const { data } = await api.get('/grades', { params: gradeParams });
      const grades = data?.data || [];
      if (IS_OPEN_GEMARA) {
        const scores = {};
        for (const sel of (row.gemaraSelection || [])) {
          const existing = grades.find((g) => g.tractate === sel.tractate) || null;
          scores[sel.tractate] = { score: existing?.score ?? '', existing };
        }
        setGradeScores(scores);
      } else {
        const existing = grades.find((g) => !g.tractate) || null;
        setGradeScores({ '': { score: existing?.score ?? '', existing } });
      }
    } catch {
      setGradeError('שגיאה בטעינת ציונים');
    } finally {
      setGradeLoading(false);
    }
  }, []);

  const handleGradeSave = async () => {
    if (!gradeDialogRow) return;
    setGradeError('');
    setGradeSaving(true);
    const memberId = gradeDialogRow.memberId?._id || gradeDialogRow.memberId;
    const examId = gradeDialogRow.examId?._id || gradeDialogRow.examId;
    const examPeriodId = extractPeriodId(gradeDialogRow.periodId);
    if (IS_OPEN_GEMARA && !examPeriodId) {
      setGradeError('לא נמצא מועד מבחן להרשמה זו');
      setGradeSaving(false);
      return;
    }
    const errs = [];
    let saved = 0;
    for (const [tractate, { score: rawScore, existing }] of Object.entries(gradeScores)) {
      if (rawScore === '' || rawScore === null || rawScore === undefined) continue;
      const s = parseFloat(rawScore);
      if (isNaN(s) || s < 0) { errs.push(`${tractate || 'ציון'}: ציון לא תקין`); continue; }
      try {
        if (existing) {
          if (existing.score === s) continue;
          await api.put(`/grades/${existing._id}`, { score: s, tractate: tractate || null, version: existing.version });
        } else {
          const body = IS_OPEN_GEMARA
            ? { memberId, examPeriodId, score: s, tractate: tractate || null }
            : { memberId, examId, score: s, tractate: tractate || null };
          await api.post('/grades', body);
        }
        saved++;
      } catch (e) {
        errs.push(e.response?.data?.error || 'שגיאה');
      }
    }
    setGradeSaving(false);
    if (errs.length === 0) {
      setSnackbar({ open: true, message: saved ? `ציון נשמר` : 'לא בוצע שינוי', severity: 'success' });
      setGradeDialogRow(null);
    } else {
      setGradeError(errs.join(' | '));
    }
  };

  const handleRegSave = async () => {
    if (selectedMembers.length === 0) return;
    if (IS_OPEN_GEMARA) {
      if (!isGemaraEntriesValid(gemaraEntries)) return;
    } else {
      if (!selectedExam) return;
    }
    setRegSaving(true);
    setRegResult(null);
    try {
      const body = IS_OPEN_GEMARA
        ? { gemaraSelection: entriesToSelection(gemaraEntries), location: regLocation, source: 'web' }
        : { examId: selectedExam._id, location: regLocation, source: 'web' };

      if (selectedMembers.length === 1) {
        await api.post('/registrations', { memberId: selectedMembers[0]._id, ...body });
        setSnackbar({ open: true, message: 'ההרשמה נוצרה בהצלחה', severity: 'success' });
        setRegOpen(false);
        loadData();
      } else {
        const { data: result } = await api.post('/registrations/bulk', {
          memberIds: selectedMembers.map((m) => m._id),
          ...body,
        });
        setRegResult(result);
        if (result.created?.length > 0) {
          setSnackbar({
            open: true,
            message: `נרשמו ${result.created.length} חברים${result.skipped?.length ? `, ${result.skipped.length} דולגו` : ''}`,
            severity: result.errors?.length ? 'warning' : 'success',
          });
          loadData();
        } else {
          setSnackbar({ open: true, message: 'לא נוצרו הרשמות חדשות', severity: 'warning' });
        }
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'שגיאה ביצירת הרשמה';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRegSaving(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/registrations', { params: { page: 1, pageSize: 100000 } });
      setAllData((res.data || []).map((r) => ({
        ...r,
        _memberFirstName: r.memberId?.firstName || '',
        _memberLastName: r.memberId?.lastName || '',
        _memberName: r.memberId ? `${r.memberId.firstName || ''} ${r.memberId.lastName || ''}`.trim() : '',
        _memberIdNumber: r.memberId?.idNumber || '',
        _memberPhone: r.memberId?.phone || '',
        _memberPhoneAlt: r.memberId?.phoneAlt || '',
        _memberCity: r.memberId?.city || '',
        _memberStreet: r.memberId?.street || '',
        _memberBuilding: r.memberId?.buildingNumber || '',
        _memberApartment: r.memberId?.apartment || '',
        _memberFatherName: r.memberId?.fatherName || '',
        _memberFatherInLawName: r.memberId?.fatherInLawName || '',
        _memberKolel: r.memberId?.kolel || r.memberId?.kolelMorning || r.memberId?.kolelAfternoon || '',
        _memberPersonalCode: r.memberId?.personalCode ?? '',
        _memberNumber: r.memberId?.memberNumber || '',
        _examCode: r.examId?.code != null ? String(r.examId.code) : '',
        _examName: r.examId?.name || '',
        _examSubject: r.examId?.subject || '',
        _examDate: r.examId?.date || '',
        _examType: r.examId?.type || '',
        _kolelCode: r.examId?.kolelCode != null ? String(r.examId.kolelCode) : '',
        _gemaraText: formatGemaraSelectionText(r.gemaraSelection) || '',
        _totalDafim: totalDafimInSelection(r.gemaraSelection),
        _examinedMark: examinedMarkLabel(r),
      })));
    } catch (err) {
      console.error('שגיאה בשליפת הרשמות:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ─── הורדת בחינה לנבחן בודד ─── */
  const handleDownloadSingle = async (row) => {
    setDownloadingId(row._id);
    try {
      const { data } = await api.post('/exam-issuance/download-single', {
        registrationId: row._id,
        options: {},
      }, { responseType: 'blob' });
      const memberName = row._memberName || 'בחינה';
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${memberName}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.status === 404 ? 'הרשמה לא נמצאה' : 'שגיאה בהורדת הבחינה';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCancel = async () => {
    try {
      await api.delete(`/registrations/${cancelTarget._id}`);
      setSnackbar({ open: true, message: 'ההרשמה בוטלה', severity: 'success' });
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'שגיאה בביטול ההרשמה', severity: 'error' });
    } finally {
      setCancelTarget(null);
    }
  };

  const columnsWithActions = useMemo(() => {
    // כשמציגים 'הכל' — מוסיפים עמודת זמן אוטומטית אם לא נבחרה ידנית
    const effectiveKeys = (periodFilter === '_all' && !visibleKeys.includes('period'))
      ? [...visibleKeys.filter(k => k !== '_actions'), 'period', '_actions']
      : visibleKeys;
    return ALL_COLUMNS
      .filter((col) => effectiveKeys.includes(col.key))
      .map((col) => {
        if (col.key === '_examinedMark') {
          return {
            ...col,
            render: (_, row) => {
              if (row.status !== 'registered') return '—';
              if (IS_OPEN_GEMARA) {
                const examinedCount = (row.gemaraSelection || []).filter((s) => s.examined).length;
                const total = (row.gemaraSelection || []).length;
                return (
                  <Button
                    size="small"
                    variant={examinedCount > 0 ? 'contained' : 'outlined'}
                    color="success"
                    onClick={() => openExamineDialog(row)}
                    sx={{ minWidth: 80, fontSize: '0.72rem' }}
                  >
                    {examinedCount > 0 ? `${examinedCount}/${total} ✔` : 'סמן נבחן'}
                  </Button>
                );
              }
              return (
                <Tooltip title={row.examined ? 'סמן כלא נבחן' : 'סמן כנבחן'}>
                  <Checkbox
                    checked={!!row.examined}
                    onChange={() => handleToggleExamined(row)}
                    size="small"
                    color="success"
                    sx={{ p: 0.3 }}
                  />
                </Tooltip>
              );
            },
          };
        }
        return col;
      })
      .concat([{
        key: '_actions', title: '', sortable: false, filterable: false,
        render: (_, row) => (
          <Stack direction="row" spacing={0.3} alignItems="center">
            {row.status === 'registered' && (
              <Tooltip title="הורד בחינה">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleDownloadSingle(row)}
                    disabled={downloadingId === row._id}
                  >
                    {downloadingId === row._id ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {row.status === 'registered' && (
              <Tooltip title="ערוך הרשמה">
                <IconButton size="small" color="info" onClick={() => handleEditOpen(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {row.status === 'registered' && (
              <Tooltip title="הזן ציון">
                <IconButton size="small" color="secondary" onClick={() => openGradeDialog(row)}>
                  <AssignmentTurnedInIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {row.status === 'registered' && (
              <Tooltip title="בטל הרשמה">
                <IconButton size="small" color="error" onClick={() => setCancelTarget(row)}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      }].filter(() => effectiveKeys.includes('_actions')));
  }, [visibleKeys, periodFilter, downloadingId, handleToggleExamined, openExamineDialog, openGradeDialog]);

  /* ─── סינון חיפוש בצד הלקוח — גם בשדות מקוננים ─── */
  const filteredData = useMemo(() => {
    let base = allData;
    if (periodFilter !== '_all') {
      if (periodFilter === '_active') {
        const active = periods.find(p => p.isActive);
        if (active) base = base.filter((row) => extractPeriodId(row.periodId) === String(active._id));
      } else {
        base = base.filter((row) => extractPeriodId(row.periodId) === String(periodFilter));
      }
    }
    if (examinedOnly) base = base.filter((row) => row._examinedMark === 'כן');
    const terms = [...pinnedSearches, activeSearch].filter(Boolean);
    if (terms.length === 0) return base;
    return base.filter((row) =>
      terms.every((term) => {
        const words = term.trim().split(/\s+/).filter(Boolean);
        return words.every((word) => {
          const lower = word.toLowerCase();
          // חיפוש בשדות ישירים + שדות מקוננים (memberId, examId)
          const searchable = [
            row._memberName, row._memberIdNumber,
            row._memberPhone, row._memberCity, row._memberKolel,
            row._examName, row._examCode, row._kolelCode,
            row._gemaraText, row._totalDafim, row._examinedMark,
            row.location, row.personalCode,
            row.status === 'registered' ? 'רשום' : row.status === 'cancelled' ? 'בוטל' : row.status,
            SOURCE_LABELS[row.source] || row.source,
            row.period,
          ];
          return searchable.some((val) =>
            String(val || '').toLowerCase().includes(lower)
          );
        });
      })
    );
  }, [allData, pinnedSearches, activeSearch, periodFilter, periods, examinedOnly]);

  /* ─── ייצוא CSV ברור (ללא קודים) ─── */
  const handleCustomExport = useCallback((includeFullDetails) => {
    // עמודות בסיסיות לפי הנראות + עמודות נוספות של פרטי חבר אם נבחר
    const baseCols = ALL_COLUMNS.filter((col) => visibleKeys.includes(col.key));
    const extraCols = includeFullDetails ? EXTRA_MEMBER_COLUMNS : [];
    // הכנסת עמודות נוספות אחרי עמודות החבר הקיימות
    const memberKeys = ['_memberFirstName', '_memberLastName', '_memberIdNumber', '_memberPhone', '_memberCity', '_memberKolel'];
    const lastMemberIdx = Math.max(...baseCols.map((c, i) => memberKeys.includes(c.key) ? i : -1));
    const exportCols = [
      ...baseCols.slice(0, lastMemberIdx + 1),
      ...extraCols,
      ...baseCols.slice(lastMemberIdx + 1),
    ];

    const header = exportCols.map((c) => `"${c.title}"`).join(',');
    const rows = filteredData.map((row) =>
      exportCols.map((col) => {
        let val;
        if (col.exportRender) {
          val = col.exportRender(row[col.key], row);
        } else {
          val = row[col.key];
        }
        if (val === null || val === undefined) val = '';
        if (typeof val === 'object') val = JSON.stringify(val);
        val = String(val).replace(/"/g, '""');
        return `"${val}"`;
      }).join(',')
    );
    const BOM = '\uFEFF';
    const csvContent = BOM + [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'הרשמות_למבחנים.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredData, visibleKeys]);

  /* ─── ייצוא CSV מפורט (תלמודא) — עמודות מסכת/מדף/עד דף נפרדות ─── */
  const handleDetailedExport = useCallback((includeFullDetails) => {
    // חישוב מקסימום מסכתות + טווחים אחרי נרמול (איחוד חופפים)
    let maxTractates = 0;
    const maxRangesPerTractate = [];
    const normalizedRows = filteredData.map((row) => ({
      row,
      gs: normalizeGemaraSelection(row.gemaraSelection),
    }));
    for (const { gs } of normalizedRows) {
      if (gs.length > maxTractates) maxTractates = gs.length;
      gs.forEach((sel, tIdx) => {
        const rc = (sel.ranges || []).length || 1;
        if (!maxRangesPerTractate[tIdx] || rc > maxRangesPerTractate[tIdx]) {
          maxRangesPerTractate[tIdx] = rc;
        }
      });
    }

    // בניית כותרות
    const baseCols = ['שם פרטי', 'שם משפחה', 'ת.ז.', 'טלפון', 'עיר', 'כולל'];
    const extraMemberHeaders = includeFullDetails
      ? EXTRA_MEMBER_COLUMNS.map((c) => c.title)
      : [];
    const afterMemberCols = ['קוד אישי', 'סטטוס', 'מקור', 'עיר בחינה', 'סה"כ דפים'];
    const gemaraHeaders = [];
    for (let t = 0; t < maxTractates; t++) {
      const suffix = maxTractates > 1 ? ` ${t + 1}` : '';
      gemaraHeaders.push(`מסכת${suffix}`);
      const ranges = maxRangesPerTractate[t] || 1;
      for (let r = 0; r < ranges; r++) {
        const rSuffix = ranges > 1 ? ` ${r + 1}` : '';
        gemaraHeaders.push(`מדף${suffix}${rSuffix}`);
        gemaraHeaders.push(`עד דף${suffix}${rSuffix}`);
      }
      gemaraHeaders.push(`סה"כ דפים${suffix}`);
    }
    const allHeaders = [...baseCols, ...extraMemberHeaders, ...afterMemberCols, ...gemaraHeaders];
    const headerLine = allHeaders.map(h => `"${h}"`).join(',');

    // בניית שורות
    const csvRows = normalizedRows.map(({ row, gs }) => {
      const summary = summarizeGemaraSelection(gs);
      const base = [
        row._memberFirstName || '',
        row._memberLastName || '',
        row._memberIdNumber || '',
        row._memberPhone || '',
        row._memberCity || '',
        row._memberKolel || '',
      ];
      const extraMemberCells = includeFullDetails
        ? EXTRA_MEMBER_COLUMNS.map((c) => row[c.key] ?? '')
        : [];
      const afterMember = [
        row.personalCode ?? '',
        row.status === 'registered' ? 'רשום' : row.status === 'cancelled' ? 'בוטל' : row.status || '',
        row.source === 'ivr' ? 'ימות' : row.source === 'web' ? 'ווב' : row.source === 'import' ? 'ייבוא' : row.source || '',
        row.location || '',
        summary.totalDafim || '',
      ];
      const gemaraCells = [];
      for (let t = 0; t < maxTractates; t++) {
        const sel = gs[t];
        gemaraCells.push(sel?.tractate || '');
        const ranges = maxRangesPerTractate[t] || 1;
        for (let r = 0; r < ranges; r++) {
          const rng = (sel?.ranges || [])[r];
          gemaraCells.push(rng?.from ? toHebrewNumeral(rng.from) : '');
          gemaraCells.push(rng?.to ? toHebrewNumeral(rng.to) : '');
        }
        const tractateTotal = sel ? countDafimInRanges(sel.ranges) : 0;
        gemaraCells.push(sel ? (tractateTotal || '') : '');
      }
      return [...base, ...extraMemberCells, ...afterMember, ...gemaraCells].map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });

    const BOM = '\uFEFF';
    const csvContent = BOM + [headerLine, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'הרשמות_מפורט.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredData]);

  /* ─── ערכים ייחודיים לסינון ─── */
  const fetchValuesFor = useCallback((colKey) => {
    if (colKey === '_actions') return undefined;
    return () => {
      const unique = [...new Set(allData.map((r) => {
        return r[colKey];
      }).filter(Boolean).map(String))];
      return Promise.resolve(unique.sort((a, b) => a.localeCompare(b, 'he')));
    };
  }, [allData]);

  // ── חישוב periodId אפקטיבי לדשבורד ───────────────────────────────────
  const effectivePeriodId = useMemo(() => {
    if (periodFilter === '_all') return null;
    if (periodFilter === '_active') {
      const active = periods.find((p) => p.isActive);
      return active?._id || null;
    }
    return periodFilter;
  }, [periodFilter, periods]);

  const effectivePeriodLabel = useMemo(() => {
    if (periodFilter === '_all') return null;
    const p = periods.find((x) => x._id === effectivePeriodId);
    return p ? p.name : null;
  }, [effectivePeriodId, periods]);

  const handleMaterialExport = useCallback(async (format) => {
    if (!IS_OPEN_GEMARA) return;
    setMaterialExporting(format);
    try {
      const params = {};
      if (effectivePeriodId) params.periodIds = String(effectivePeriodId);
      const path = format === 'pdf'
        ? '/registrations/export-material-pdf'
        : '/registrations/export-material-excel';
      const response = await api.get(path, { params, responseType: 'blob' });
      const day = new Date().toISOString().slice(0, 10);
      const mime = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const filename = format === 'pdf'
        ? `סיכום_עותקים_תלמודא_${day}.pdf`
        : `סיכום_עותקים_תלמודא_${day}.xlsx`;
      const url = URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('שגיאה בייצוא סיכום עותקים:', err);
      setSnackbar({ open: true, message: 'שגיאה בייצוא סיכום עותקים', severity: 'error' });
    } finally {
      setMaterialExporting('');
    }
  }, [effectivePeriodId]);

  const openExaminedExportDialog = useCallback(() => {
    if (!effectivePeriodId) {
      setSnackbar({ open: true, message: 'יש לבחור תקופת מבחן לייצוא', severity: 'warning' });
      return;
    }
    setExaminedExportMode('examined');
    setExaminedExportFormat('excel');
    setExaminedExportFileMode('single');
    setExaminedExportColumns([...DEFAULT_EXAMINED_EXPORT_COLUMNS]);
    setExaminedExtraBlankCols([]);
    setExaminedNewBlankCol('');
    setExaminedExportOpen(true);
  }, [effectivePeriodId]);

  const toggleExaminedExportColumn = useCallback((key) => {
    setExaminedExportColumns((prev) => (
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
  }, []);

  const addExaminedBlankColumn = useCallback(() => {
    const name = examinedNewBlankCol.trim().slice(0, 40);
    if (!name) return;
    setExaminedExtraBlankCols((prev) => (
      prev.includes(name) ? prev : [...prev, name]
    ));
    setExaminedNewBlankCol('');
  }, [examinedNewBlankCol]);

  const handleExaminedExport = useCallback(async () => {
    if (!effectivePeriodId) {
      setSnackbar({ open: true, message: 'יש לבחור תקופת מבחן לייצוא', severity: 'warning' });
      return;
    }
    if (examinedExportColumns.length === 0 && examinedExtraBlankCols.length === 0) {
      setSnackbar({ open: true, message: 'יש לבחור לפחות עמודה אחת', severity: 'warning' });
      return;
    }
    const exportMode = examinedExportMode === 'graded' ? 'graded' : 'examined';
    const format = examinedExportFormat === 'pdf' ? 'pdf' : 'excel';
    const fileMode = examinedExportFileMode === 'byExam' || examinedExportFileMode === 'bySubject'
      ? examinedExportFileMode
      : 'single';
    const isZip = fileMode === 'byExam' || fileMode === 'bySubject';
    const path = format === 'pdf'
      ? '/registrations/export-examined-pdf'
      : '/registrations/export-examined-excel';
    const orderedColumns = EXAMINED_EXPORT_COLUMNS
      .map((c) => c.key)
      .filter((k) => examinedExportColumns.includes(k));
    setExaminedExporting(true);
    try {
      const response = await api.get(path, {
        params: {
          periodId: String(effectivePeriodId),
          mode: exportMode,
          fileMode,
          columns: orderedColumns.join(','),
          extraBlankColumns: examinedExtraBlankCols.join(','),
        },
        responseType: 'blob',
      });
      const day = new Date().toISOString().slice(0, 10);
      const periodPart = (effectivePeriodLabel || 'תקופה').replace(/[\\/:*?"<>|]/g, '_');
      const prefix = exportMode === 'graded' ? 'עם_ציונים' : 'נבחנים';
      let filename;
      let mime;
      if (isZip) {
        const zipKind = fileMode === 'bySubject' ? 'לפי_נושא' : 'לפי_קוד_מבחן';
        filename = `${prefix}_${zipKind}_${periodPart}_${day}.zip`;
        mime = 'application/zip';
      } else {
        const ext = format === 'pdf' ? 'pdf' : 'xlsx';
        mime = format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        filename = `${prefix}_תקופה_${periodPart}_${day}.${ext}`;
      }
      const url = URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      setExaminedExportOpen(false);
    } catch (err) {
      console.error('שגיאה בייצוא נבחנים:', err);
      setSnackbar({ open: true, message: 'שגיאה בייצוא נבחנים', severity: 'error' });
    } finally {
      setExaminedExporting(false);
    }
  }, [
    effectivePeriodId,
    effectivePeriodLabel,
    examinedExportMode,
    examinedExportFormat,
    examinedExportFileMode,
    examinedExportColumns,
    examinedExtraBlankCols,
  ]);

  return (
    <Box>
      <AdminPageHeader
        title="הרשמות למבחנים"
        sub={effectivePeriodLabel || undefined}
        primary={{ label: 'הרשמה חדשה', onClick: handleRegOpen }}
        actions={(
          <ToggleButtonGroup
            value={periodFilter}
            exclusive
            onChange={(_, v) => v && setPeriodFilter(v)}
            size="small"
          >
            {periods.map((p) => (
              <ToggleButton key={p._id} value={p._id}>
                {p.name}{p.isActive ? ' ●' : ''}
              </ToggleButton>
            ))}
            <ToggleButton value="_all">הכל</ToggleButton>
          </ToggleButtonGroup>
        )}
        legacy={(
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>הרשמות למבחנים</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <ToggleButtonGroup
                value={periodFilter}
                exclusive
                onChange={(_, v) => v && setPeriodFilter(v)}
                size="small"
              >
                {periods.map((p) => (
                  <ToggleButton key={p._id} value={p._id}>
                    {p.name}{p.isActive ? ' ●' : ''}
                  </ToggleButton>
                ))}
                <ToggleButton value="_all">הכל</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="contained" color="secondary" onClick={handleRegOpen}>+ הרשמה חדשה</Button>
            </Box>
          </Box>
        )}
      />

      {/* ── דשבורד רישומים ──────────────────────────────────────── */}
      <RegistrationsDashboard periodId={effectivePeriodId} periodLabel={effectivePeriodLabel} />

      {/* חיפוש + כפתורי עמודות */}
      <Box sx={{ mb: 2 }}>
        <Box
          component="form"
          onSubmit={(e) => { e.preventDefault(); pinSearch(); }}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <TextField
            size="small"
            placeholder="חיפוש לפי שם, כולל, ת.ז., עיר..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); pinSearch(); } }}
            sx={{ width: 300 }}
          />
          <Tooltip title="נעץ מונח חיפוש (Enter)">
            <span>
              <Button type="submit" variant="contained" size="small" disabled={!searchInput.trim()}>
                📌 נעץ
              </Button>
            </span>
          </Tooltip>
          {(pinnedSearches.length > 0 || searchInput.trim()) && (
            <Button variant="text" size="small" color="error" onClick={clearAllSearch}>
              ✕ נקה חיפוש
            </Button>
          )}

          <Tooltip title="הצג רק נרשמים שסומנו כנבחנים">
            <Button
              variant={examinedOnly ? 'contained' : 'outlined'}
              color="success"
              size="small"
              startIcon={<AssignmentTurnedInIcon />}
              onClick={() => setExaminedOnly((v) => !v)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              נבחנים
            </Button>
          </Tooltip>

          <Button variant="outlined" size="small" onClick={() => { setExportFullDetails(false); setExportDialogOpen(true); }} disabled={filteredData.length === 0}>
            ייצוא CSV
          </Button>

          <Tooltip title={!effectivePeriodId ? 'יש לבחור תקופת מבחן' : 'ייצוא נבחנים לאקסל/PDF עם עמודות ציון לבודקים'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={openExaminedExportDialog}
                disabled={!effectivePeriodId || examinedExporting}
              >
                {examinedExporting ? 'מייצא…' : 'ייצוא נבחנים'}
              </Button>
            </span>
          </Tooltip>

          {IS_OPEN_GEMARA && (
            <Button variant="outlined" size="small" color="secondary" onClick={() => { setExportFullDetails(false); setExportDialogOpen('detailed'); }} disabled={filteredData.length === 0}>
              ייצוא מפורט
            </Button>
          )}

          {IS_OPEN_GEMARA && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={() => handleMaterialExport('excel')}
                disabled={!!materialExporting}
              >
                {materialExporting === 'excel' ? 'מייצא…' : 'סיכום עותקים Excel'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={<PictureAsPdfOutlinedIcon />}
                onClick={() => handleMaterialExport('pdf')}
                disabled={!!materialExporting}
              >
                {materialExporting === 'pdf' ? 'מייצא…' : 'סיכום עותקים PDF'}
              </Button>
            </>
          )}

          {/* כפתור בחירת עמודות */}
          <Tooltip title="בחר עמודות להצגה">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} color="primary">
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{ sx: { maxHeight: 480, width: 260 } }}
          >
            <MenuItem dense onClick={showAll}>
              <CheckCircleOutlineIcon sx={{ fontSize: 18, mr: 1, color: 'var(--ck-primary, #1565c0)' }} />
              <ListItemText primary="הצג הכל" />
            </MenuItem>
            <MenuItem dense onClick={resetDefaults}>
              <RestoreIcon sx={{ fontSize: 18, mr: 1, color: 'var(--ck-primary, #1565c0)' }} />
              <ListItemText primary="ברירת מחדל" />
            </MenuItem>
            <Divider />
            {ALL_COLUMNS.map((col) => (
              <MenuItem key={col.key} dense onClick={() => toggleColumn(col.key)}>
                <Checkbox checked={visibleKeys.includes(col.key)} size="small" sx={{ p: 0, mr: 1 }} />
                <ListItemText primary={col.title} />
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Chips — מונחי חיפוש נעוצים */}
        {pinnedSearches.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
            {pinnedSearches.map((p) => (
              <Chip
                key={p}
                label={p}
                size="small"
                color="primary"
                onDelete={() => removePinnedSearch(p)}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Box>
        )}
      </Box>

      <TableKit
        columns={columnsWithActions}
        data={filteredData}
        total={filteredData.length}
        loading={loading}
        clientSideMode
        showColumnPicker={false}
        showExport={false}
        emptyMessage={examinedOnly ? 'אין נבחנים להצגה' : 'אין הרשמות'}
        exportFileName="הרשמות_למבחנים"
        fetchValuesFor={fetchValuesFor}
      />

      {/* ─── דיאלוג אפשרויות ייצוא ─── */}
      <Dialog open={!!exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>ייצוא הרשמות</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {filteredData.length} הרשמות ייוצאו לקובץ CSV.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={exportFullDetails}
                onChange={(e) => setExportFullDetails(e.target.checked)}
              />
            }
            label="כלול את כל פרטי הנרשם (ת.ז., טלפון, כתובת, כולל ועוד)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>ביטול</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (exportDialogOpen === 'detailed') {
                handleDetailedExport(exportFullDetails);
              } else {
                handleCustomExport(exportFullDetails);
              }
              setExportDialogOpen(false);
            }}
          >
            ייצא
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── דיאלוג ייצוא נבחנים (אקסל / PDF + עמודות ציון) ─── */}
      <Dialog
        open={examinedExportOpen}
        onClose={() => !examinedExporting && setExaminedExportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ייצוא נבחנים</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            רשימת מי שסומן כנבחן בתקופה הנבחרת — מחולקת לפי מבחן, עם עמודות ריקות לציון לבודקים.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>מי לייצא</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={examinedExportMode}
            onChange={(_, v) => v && setExaminedExportMode(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="examined">נבחנים</ToggleButton>
            <ToggleButton value="graded">עם ציונים</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>פורמט</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={examinedExportFormat}
            onChange={(_, v) => v && setExaminedExportFormat(v)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="excel">אקסל</ToggleButton>
            <ToggleButton value="pdf">PDF</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>מצב קבצים</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={examinedExportFileMode}
            onChange={(_, v) => v && setExaminedExportFileMode(v)}
            sx={{ mb: 1, flexWrap: 'wrap' }}
          >
            <ToggleButton value="single">קובץ אחד</ToggleButton>
            <ToggleButton value="byExam">לפי קוד מבחן</ToggleButton>
            <ToggleButton value="bySubject">לפי נושא</ToggleButton>
          </ToggleButtonGroup>
          {(examinedExportFileMode === 'byExam' || examinedExportFileMode === 'bySubject') && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              {examinedExportFileMode === 'bySubject'
                ? `יורד ZIP — קובץ ${examinedExportFormat === 'pdf' ? 'PDF' : 'אקסל'} לכל נושא (שבת, נידה וכו׳), לחלוקה לבודקים.`
                : `יורד ZIP — קובץ ${examinedExportFormat === 'pdf' ? 'PDF' : 'אקסל'} לכל קוד מבחן.`}
            </Typography>
          )}
          {examinedExportFileMode === 'single' && <Box sx={{ mb: 1 }} />}

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>עמודות</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {EXAMINED_EXPORT_COLUMNS.map((col) => (
              <FormControlLabel
                key={col.key}
                sx={{ mr: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={examinedExportColumns.includes(col.key)}
                    onChange={() => toggleExaminedExportColumn(col.key)}
                  />
                }
                label={col.title}
              />
            ))}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>עמודות ריקות נוספות לבודקים</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <TextField
              size="small"
              placeholder="למשל: ציון חלק ב"
              value={examinedNewBlankCol}
              onChange={(e) => setExaminedNewBlankCol(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addExaminedBlankColumn();
                }
              }}
              sx={{ flex: 1 }}
            />
            <Button variant="outlined" size="small" onClick={addExaminedBlankColumn} disabled={!examinedNewBlankCol.trim()}>
              הוסף
            </Button>
          </Box>
          {examinedExtraBlankCols.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1 }}>
              {examinedExtraBlankCols.map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  onDelete={() => setExaminedExtraBlankCols((prev) => prev.filter((n) => n !== name))}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExaminedExportOpen(false)} disabled={examinedExporting}>ביטול</Button>
          <Button
            variant="contained"
            onClick={handleExaminedExport}
            disabled={examinedExporting || (examinedExportColumns.length === 0 && examinedExtraBlankCols.length === 0)}
            startIcon={examinedExporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
          >
            {examinedExporting ? 'מייצא…' : 'ייצא'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── דיאלוג סימון נבחן — תלמודא (טווחים + נבחן) ─── */}
      {IS_OPEN_GEMARA && (
        <GemaraExamineEditDialog
          key={examineDialogRow?._id || 'examine'}
          open={!!examineDialogRow}
          memberName={examineDialogRow?._memberName}
          initialSelection={examineDialogRow?.gemaraSelection || []}
          onSave={handleExamineDialogSave}
          saving={examineSaving}
          onClose={() => !examineSaving && setExamineDialogRow(null)}
        />
      )}

      {/* דיאלוג ביטול */}
      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)}>
        <DialogTitle>ביטול הרשמה</DialogTitle>
        <DialogContent>
          <Typography>
            לבטל את הרשמת{' '}
            <strong>
              {cancelTarget?.memberId
                ? `${cancelTarget.memberId.firstName} ${cancelTarget.memberId.lastName}`
                : ''}
            </strong>
            {cancelTarget?.examId ? ` למבחן ${cancelTarget.examId.name}` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>ביטול</Button>
          <Button color="error" variant="contained" onClick={handleCancel}>בטל הרשמה</Button>
        </DialogActions>
      </Dialog>

      {/* דיאלוג עריכת הרשמה */}
      <Dialog open={!!editTarget} onClose={() => !editSaving && setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>עריכת הרשמה — {editTarget?._memberName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {IS_OPEN_GEMARA ? (
              <GemaraSelectionEditor
                value={editGemaraEntries}
                onChange={setEditGemaraEntries}
                showExamined
                title="עריכת חומר לבחינה"
              />
            ) : (
              <Autocomplete
                options={editExamOptions}
                getOptionLabel={(o) => `${o.name} (${o.code}) — ${formatHebrewDate(o.date)}`}
                value={editExam}
                onChange={(_, v) => setEditExam(v)}
                isOptionEqualToValue={(o, v) => o._id === v?._id}
                renderInput={(params) => <TextField {...params} label="מבחן" size="small" />}
                noOptionsText="אין מבחנים פתוחים"
              />
            )}

            <Autocomplete
              options={examCities}
              value={editLocation || null}
              onChange={(_, v) => setEditLocation(v || '')}
              renderInput={(params) => <TextField {...params} label="עיר בחינה" size="small" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editSaving}>ביטול</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving || (IS_OPEN_GEMARA ? !isGemaraEntriesValid(editGemaraEntries) : false)}
          >
            {editSaving ? <CircularProgress size={20} /> : 'שמור שינויים'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── דיאלוג ציון להרשמה ─── */}
      <Dialog open={!!gradeDialogRow} onClose={() => !gradeSaving && setGradeDialogRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>ציון — {gradeDialogRow?._memberName}</DialogTitle>
        <DialogContent>
          {gradeLoading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>}
          {!gradeLoading && gradeError && <Alert severity="error" sx={{ mb: 1 }}>{gradeError}</Alert>}
          {!gradeLoading && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {IS_OPEN_GEMARA
                ? Object.entries(gradeScores).map(([tractate, { score, existing }]) => (
                    <Box key={tractate} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2" sx={{ minWidth: 110, fontWeight: 600 }}>{tractate}</Typography>
                      <TextField
                        label="ציון"
                        type="number"
                        size="small"
                        value={score}
                        onChange={(e) => setGradeScores((prev) => ({ ...prev, [tractate]: { ...prev[tractate], score: e.target.value } }))}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ width: 120 }}
                      />
                      {existing != null && (
                        <Typography variant="caption" color="text.secondary">קיים: {existing.score}</Typography>
                      )}
                    </Box>
                  ))
                : (() => {
                    const entry = gradeScores[''] || { score: '', existing: null };
                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">{gradeDialogRow?._examName}</Typography>
                        <TextField
                          label="ציון"
                          type="number"
                          size="small"
                          value={entry.score}
                          onChange={(e) => setGradeScores({ '': { ...entry, score: e.target.value } })}
                          inputProps={{ min: 0, step: 1 }}
                          sx={{ width: 160 }}
                          autoFocus
                        />
                        {entry.existing != null && (
                          <Typography variant="caption" color="text.secondary">ציון קיים: {entry.existing.score}</Typography>
                        )}
                      </Box>
                    );
                  })()
              }
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGradeDialogRow(null)} disabled={gradeSaving}>ביטול</Button>
          <Button variant="contained" onClick={handleGradeSave} disabled={gradeSaving || gradeLoading}>
            {gradeSaving ? <CircularProgress size={20} /> : 'שמור'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ─── דיאלוג הרשמה חדשה ─── */}
      <Dialog open={regOpen} onClose={() => !regSaving && setRegOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>הרשמה למבחן</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {IS_OPEN_GEMARA ? (
              <GemaraSelectionEditor
                value={gemaraEntries}
                onChange={setGemaraEntries}
                showExamined={false}
                title="בחירת חומר לבחינה"
              />
            ) : (
              <Autocomplete
                options={examOptions}
                getOptionLabel={(o) => `${o.name} (${o.code}) — ${formatHebrewDate(o.date)}`}
                value={selectedExam}
                onChange={(_, v) => setSelectedExam(v)}
                isOptionEqualToValue={(o, v) => o._id === v?._id}
                renderInput={(params) => <TextField {...params} label="מבחן" size="small" required />}
                noOptionsText="אין מבחנים פתוחים"
              />
            )}

            <Autocomplete
              multiple
              options={memberOptions}
              getOptionLabel={(o) => `${o.firstName} ${o.lastName}${o.phone ? ` (${o.phone})` : ''}${o.idNumber ? ` — ת.ז. ${o.idNumber}` : ''}`}
              value={selectedMembers}
              onChange={(_, v) => setSelectedMembers(v)}
              onInputChange={(_, v) => setMemberSearch(v)}
              isOptionEqualToValue={(o, v) => o._id === v?._id}
              filterOptions={(x) => x}
              loading={memberLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="חברים"
                  size="small"
                  required
                  placeholder="הקלד שם או טלפון לחיפוש..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {memberLoading && <CircularProgress color="inherit" size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="לא נמצאו חברים"
            />

            <Autocomplete
              options={examCities}
              value={regLocation || null}
              onChange={(_, v) => setRegLocation(v || '')}
              renderInput={(params) => <TextField {...params} label="עיר בחינה" size="small" />}
            />

            {selectedMembers.length > 1 && (
              <Alert severity="info">
                ייווצרו {selectedMembers.length} הרשמות בבת אחת
              </Alert>
            )}

            {regResult && (
              <Box>
                {regResult.created?.length > 0 && (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    נרשמו: {regResult.created.map((c) => c.name).join(', ')}
                  </Alert>
                )}
                {regResult.skipped?.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    דולגו (כבר רשומים): {regResult.skipped.map((s) => s.name).join(', ')}
                  </Alert>
                )}
                {regResult.errors?.length > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    שגיאות: {regResult.errors.map((e) => `${e.memberId}: ${e.error}`).join(', ')}
                  </Alert>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegOpen(false)} disabled={regSaving}>סגור</Button>
          <Button
            variant="contained"
            onClick={handleRegSave}
            disabled={regSaving || selectedMembers.length === 0 || (IS_OPEN_GEMARA ? !isGemaraEntriesValid(gemaraEntries) : !selectedExam)}
          >
            {regSaving ? <CircularProgress size={20} /> : `רשום ${selectedMembers.length > 0 ? `(${selectedMembers.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── ייבוא הרשמות ─── */}
      {(() => {
        return (
          <ImportSection
            label="הרשמות"
            endpoint="/import/registrations"
            templateEndpoint="/import/template/registrations"
            description="ייבוא הרשמות מקובץ Excel. מבנה אופקי: קוד | שם | משפחת | טלפון | מסכת | מדף | עד דף | מדף | עד דף ... דפים במספרים."
            onImportDone={() => loadData()}
            uploadDisabled={!importPeriodId}
            uploadDisabledReason="יש לבחור זמן מבחנים לפני הייבוא"
            extraFormData={{
              registeredAt: importDate,
              periodId: importPeriodId,
              ...(importUpdateMode ? { updateMode: 'true' } : {}),
            }}
            extraActions={
              <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                  <TextField
                    label="תאריך רישום"
                    type="date"
                    size="small"
                    value={importDate}
                    onChange={(e) => setImportDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 200 }}
                    disabled={importUpdateMode}
                  />
                  <TextField
                    label="זמן מבחנים (חובה)"
                    select
                    size="small"
                    value={importPeriodId}
                    onChange={(e) => setImportPeriodId(e.target.value)}
                    sx={{ width: 220 }}
                    error={!importPeriodId}
                    helperText={!importPeriodId ? 'יש לבחור זמן מבחנים' : ''}
                  >
                    {periods.map((p) => (
                      <MenuItem key={p._id} value={p._id}>{p.name}{p.isActive ? ' (פעיל)' : ''}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={importUpdateMode}
                      onChange={(e) => setImportUpdateMode(e.target.checked)}
                      size="small"
                      color="warning"
                    />
                  }
                  label={
                    <Typography variant="caption" color={importUpdateMode ? 'warning.main' : 'text.secondary'}>
                      עדכון הרשמות קיימות (עדכן מדף / עד דף בלבד — לא יוצר הרשמות חדשות)
                    </Typography>
                  }
                />
              </Stack>
            }
          />
        );
      })()}
    </Box>
  );
}
